'use client';

import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Users, ArrowUpDown, Search, Network } from 'lucide-react';
import { useSportsStore } from '@/store/useSportsStore';
import { TournamentStatus, getStatusLabel } from '@/utils/tournament-status';
import { TournamentSortOption, getSortLabel } from '@/utils/tournament-sort';
import { TournamentFormat } from '@/types/tournament.model';
import { getTournamentFormatLabel } from '@/utils/tournament-format';
import type { Sport } from '@/types/sports.model';

interface TournamentFiltersProps {
  selectedSports: string[];
  selectedStatuses: TournamentStatus[];
  selectedFormats: TournamentFormat[];
  sortOption: TournamentSortOption;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSportsChange: (sports: string[]) => void;
  onStatusesChange: (statuses: TournamentStatus[]) => void;
  onFormatsChange: (formats: TournamentFormat[]) => void;
  onSortChange: (sort: TournamentSortOption) => void;
}

export function TournamentFilters({
  selectedSports,
  selectedStatuses,
  selectedFormats,
  sortOption,
  searchQuery,
  onSearchChange,
  onSportsChange,
  onStatusesChange,
  onFormatsChange,
  onSortChange,
}: TournamentFiltersProps) {
  const { sportsData, fetchSports, isInitialized } = useSportsStore();
  const [sports, setSports] = useState<Sport[]>([]);

  useEffect(() => {
    if (!isInitialized) {
      fetchSports();
    }
  }, [isInitialized, fetchSports]);

  useEffect(() => {
    if (sportsData.length > 0) {
      setSports(sportsData.map((sd) => sd.sport));
    }
  }, [sportsData]);

  const handleSportToggle = (sportUid: string) => {
    const newSelected = selectedSports.includes(sportUid)
      ? selectedSports.filter((s) => s !== sportUid)
      : [...selectedSports, sportUid];
    onSportsChange(newSelected);
  };

  const handleStatusToggle = (status: TournamentStatus) => {
    const newSelected = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status];
    onStatusesChange(newSelected);
  };

  const handleFormatToggle = (format: TournamentFormat) => {
    const newSelected = selectedFormats.includes(format)
      ? selectedFormats.filter((f) => f !== format)
      : [...selectedFormats, format];
    onFormatsChange(newSelected);
  };

  const allStatuses = [
    TournamentStatus.OPEN,
    TournamentStatus.IN_PROGRESS,
    TournamentStatus.FINISHED,
    TournamentStatus.CANCELLED,
  ];

  const allSortOptions = [
    TournamentSortOption.START_DATE_ASC,
    TournamentSortOption.START_DATE_DESC,
    TournamentSortOption.NAME_ASC,
    TournamentSortOption.NAME_DESC,
    TournamentSortOption.SIGNUP_DEADLINE_ASC,
  ];

  // LONG is not offered: it cannot be created from the wizard.
  const allFormats = [TournamentFormat.SHORT, TournamentFormat.AMERICAN];

  const getStatusButtonStyle = (status: TournamentStatus) => {
    const isSelected = selectedStatuses.includes(status);

    if (!isSelected) {
      return 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100';
    }

    switch (status) {
      case TournamentStatus.OPEN:
        return 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200';
      case TournamentStatus.IN_PROGRESS:
        return 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200';
      case TournamentStatus.FINISHED:
        return 'bg-gray-200 text-gray-800 border-gray-400 hover:bg-gray-300';
      case TournamentStatus.CANCELLED:
        return 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200';
      default:
        return 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100';
    }
  };

  return (
    <div className="space-y-3">
      {/* Search Input - Full width on mobile */}
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <Input
          className="h-9 w-full border-gray-300 bg-white pl-10 placeholder:text-gray-500"
          placeholder="Buscar torneos por nombre"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {/* Status Filters */}
      <div className="flex w-full flex-wrap gap-2">
        {allStatuses.map((status) => (
          <Button
            key={status}
            variant="outline"
            size="sm"
            className={`shrink-0 transition-colors ${getStatusButtonStyle(status)}`}
            onClick={() => handleStatusToggle(status)}
          >
            {getStatusLabel(status)}
          </Button>
        ))}
      </div>

      {/* Sort and Sports Filter - Full width on mobile */}
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        {/* Sort Dropdown */}
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <ArrowUpDown className="h-4 w-4 shrink-0 text-gray-500" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex w-full items-center justify-between gap-2 border-gray-300 sm:w-auto sm:justify-start"
              >
                <span className="text-sm">{getSortLabel(sortOption)}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuRadioGroup
                value={sortOption}
                onValueChange={(value) =>
                  onSortChange(value as TournamentSortOption)
                }
              >
                {allSortOptions.map((option) => (
                  <DropdownMenuRadioItem key={option} value={option}>
                    {getSortLabel(option)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Sports Filter */}
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Users className="h-4 w-4 shrink-0 text-gray-500" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex w-full items-center justify-between gap-2 border-gray-300 sm:w-auto sm:justify-start"
              >
                <span className="text-sm">
                  {selectedSports.length === 0
                    ? 'Todos los deportes'
                    : selectedSports.length === 1
                      ? sports.find((s) => s.uid === selectedSports[0])?.name ||
                        'Deporte'
                      : `${selectedSports.length} deportes`}
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {sports.map((sport) => (
                <DropdownMenuCheckboxItem
                  key={sport.uid}
                  checked={selectedSports.includes(sport.uid)}
                  onCheckedChange={() => handleSportToggle(sport.uid)}
                >
                  {sport.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Format Filter */}
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Network className="h-4 w-4 shrink-0 text-gray-500" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex w-full items-center justify-between gap-2 border-gray-300 sm:w-auto sm:justify-start"
              >
                <span className="text-sm">
                  {selectedFormats.length === 0
                    ? 'Todos los formatos'
                    : selectedFormats.length === 1
                      ? getTournamentFormatLabel(selectedFormats[0])
                      : `${selectedFormats.length} formatos`}
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {allFormats.map((format) => (
                <DropdownMenuCheckboxItem
                  key={format}
                  checked={selectedFormats.includes(format)}
                  onCheckedChange={() => handleFormatToggle(format)}
                >
                  {getTournamentFormatLabel(format)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Selected Sports Badges */}
        {selectedSports.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="hidden h-6 w-px bg-gray-300 sm:block" />
            {selectedSports.map((sportUid) => {
              const sport = sports.find((s) => s.uid === sportUid);
              return sport ? (
                <Badge
                  key={sportUid}
                  variant="outline"
                  className="cursor-pointer border-green-300 bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800"
                  onClick={() => handleSportToggle(sportUid)}
                >
                  {sport.name}
                  <span className="ml-1 font-bold">×</span>
                </Badge>
              ) : null;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
